// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Dropdown } from 'semantic-ui-react';

interface NavItem {
  label: string;
  path: string;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

type NavEntry = NavItem | NavGroup;

function isNavGroup(entry: NavEntry): entry is NavGroup {
  return 'items' in entry;
}

const navConfig: NavEntry[] = [
  { label: 'Brokerages', path: '/brokerages' },
  { label: 'Branding', path: '/branding' },
  {
    label: 'Advisors',
    items: [
      { label: 'All Advisors', path: '/advisors' },
      { label: 'Support Staff', path: '/support-staff' },
    ],
  },
  { label: 'Providers', path: '/providers' },
  { label: 'Policy Types', path: '/policy-types' },
  { label: 'Quote Terms', path: '/quote-terms' },
  { label: 'New Business Templates', path: '/new-business-templates' },
  {
    label: 'Templates',
    items: [
      { label: 'Template Management', path: '/templates' },
      { label: 'Template Preview', path: '/template-preview' },
      { label: 'Template Usage', path: '/template-usage' },
    ],
  },
  {
    label: 'Email',
    items: [
      { label: 'Email Templates', path: '/email-templates' },
      { label: 'Email Notifications', path: '/email-notifications' },
    ],
  },
  { label: 'Audit Log', path: '/audit-log' },
  {
    label: 'Funds',
    items: [
      { label: 'Retrieval History', path: '/funds/retrieval-history' },
    ],
  },
  {
    label: 'Intelligence',
    items: [
      { label: 'Configuration', path: '/intelligence-config' },
      { label: 'Profile Form Schemas', path: '/profile-forms-config' },
      { label: 'Tax Tables', path: '/tax-tables' },
      { label: 'Projection Assumptions', path: '/projection-assumptions' },
    ],
  },
  {
    label: 'Tools',
    items: [
      { label: 'Branding Migration', path: '/branding-migration' },
      { label: 'Brokerage Backfill', path: '/brokerage-backfill' },
    ],
  },
];

export default function Navigation() {
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };

  const isGroupActive = (items: NavItem[]) => {
    return items.some((item) => isActive(item.path));
  };

  const renderNavItem = (item: NavItem) => {
    const active = isActive(item.path);

    return (
      <NavLink
        key={item.path}
        to={item.path}
        className="nav-link"
        style={{
          padding: '0.75rem 1rem',
          color: active ? '#2185D0' : '#1b1c1d',
          fontWeight: active ? 600 : 400,
          textDecoration: 'none',
          borderBottom: active ? '2px solid #2185D0' : '2px solid transparent',
          transition: 'all 0.2s ease',
          fontSize: '0.9rem',
        }}
        onMouseEnter={(e) => {
          if (!active) {
            e.currentTarget.style.color = '#2185D0';
            e.currentTarget.style.backgroundColor = 'rgba(33, 133, 208, 0.05)';
          }
        }}
        onMouseLeave={(e) => {
          if (!active) {
            e.currentTarget.style.color = '#1b1c1d';
            e.currentTarget.style.backgroundColor = 'transparent';
          }
        }}
        aria-current={active ? 'page' : undefined}
      >
        {item.label}
      </NavLink>
    );
  };

  const renderNavGroup = (group: NavGroup) => {
    const groupActive = isGroupActive(group.items);

    const dropdownOptions = group.items.map((item) => ({
      key: item.path,
      text: item.label,
      value: item.path,
      active: isActive(item.path),
    }));

    return (
      <Dropdown
        key={group.label}
        className="nav-dropdown"
        trigger={
          <button
            type="button"
            className="nav-link nav-dropdown-trigger"
            style={{
              padding: '0.75rem 1rem',
              color: groupActive ? '#2185D0' : '#1b1c1d',
              fontWeight: groupActive ? 600 : 400,
              textDecoration: 'none',
              borderBottom: groupActive ? '2px solid #2185D0' : '2px solid transparent',
              transition: 'all 0.2s ease',
              fontSize: '0.9rem',
              cursor: 'pointer',
              background: 'transparent',
              border: 'none',
            }}
            onMouseEnter={(e) => {
              if (!groupActive) {
                e.currentTarget.style.color = '#2185D0';
                e.currentTarget.style.backgroundColor = 'rgba(33, 133, 208, 0.05)';
              }
            }}
            onMouseLeave={(e) => {
              if (!groupActive) {
                e.currentTarget.style.color = '#1b1c1d';
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
          >
            {group.label}
          </button>
        }
        options={dropdownOptions}
        pointing="top left"
        onChange={(_, data) => {
          navigate(data.value as string);
        }}
      />
    );
  };

  return (
    <nav
      className="top-banner-nav"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.25rem',
      }}
      role="navigation"
      aria-label="Main navigation"
    >
      {navConfig.map((entry) => {
        if (isNavGroup(entry)) {
          return renderNavGroup(entry);
        }
        return renderNavItem(entry);
      })}
    </nav>
  );
}
